import { useEffect, useState } from "react";

import { apiClient } from "src/api/client";
import { Company } from "src/api/types";
import { normalizeRole } from "src/constants/roles";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { setClientCompanies } from "src/store/slices/selectedClientSlice";

type CompaniesState = {
  clientId?: number;
  companies: Company[];
  loading: boolean;
  error: string | null;
};

const emptyState: CompaniesState = {
  companies: [],
  loading: false,
  error: null,
};

export const useClientCompanies = (overrideClientId?: number) => {
  const dispatch = useAppDispatch();
  const selectedClientId = useAppSelector(
    (state) => state.selectedClient.client?.id,
  );
  const authUser = useAppSelector((state) => state.auth.user);
  const [state, setState] = useState<CompaniesState>(emptyState);
  const [reloadVersion, setReloadVersion] = useState(0);

  const isRegular = authUser?.type
    ? normalizeRole(authUser.type) === "regular"
    : false;
  const targetClientId =
    overrideClientId !== undefined
      ? overrideClientId
      : isRegular
        ? authUser?.id
        : selectedClientId;

  useEffect(() => {
    const handleCompanyCreated = () => {
      setReloadVersion((current) => current + 1);
    };

    window.addEventListener("companyCreated", handleCompanyCreated);
    return () => {
      window.removeEventListener("companyCreated", handleCompanyCreated);
    };
  }, []);

  useEffect(() => {
    if (targetClientId === undefined) {
      setState(emptyState);
      return;
    }

    let active = true;
    setState({
      clientId: targetClientId,
      companies: [],
      loading: true,
      error: null,
    });

    apiClient
      .getUserCompanies(targetClientId)
      .then((companies) => {
        if (!active) return;

        setState({
          clientId: targetClientId,
          companies,
          loading: false,
          error: null,
        });

        if (
          overrideClientId === undefined &&
          selectedClientId === targetClientId
        ) {
          dispatch(setClientCompanies({ clientId: targetClientId, companies }));
        }
      })
      .catch((error: unknown) => {
        if (!active) return;
        setState({
          clientId: targetClientId,
          companies: [],
          loading: false,
          error:
            error instanceof Error
              ? error.message
              : "Failed to load companies",
        });
      });

    return () => {
      active = false;
    };
  }, [
    dispatch,
    overrideClientId,
    reloadVersion,
    selectedClientId,
    targetClientId,
  ]);

  const hasCurrentClientData = state.clientId === targetClientId;

  return {
    companies: hasCurrentClientData ? state.companies : [],
    loading:
      targetClientId !== undefined &&
      (!hasCurrentClientData || state.loading),
    error: hasCurrentClientData ? state.error : null,
  };
};
